import { useState, useEffect, useRef } from 'preact/hooks';
import IconButton from '../../button/IconButton';
import Icon from '../../icon/Icon';
import './AssistantHeaderSearchInput.less';
import useAppDispatch from '../../../hooks/useAppDispatch';
import useAppSelector from '../../../hooks/useAppSelector';
import { useTranslation } from '../../../hooks/useTranslation';
import ImageUpload from '../../main/ImageUpload';

const examplePlaceholders = [
    'Vestidos de verano para una cena especial',
    'Blusas o tops de fiesta con colores vivos',
    'Chaqueta oscura para un look moderno',
    'Accesorios para regalar a mamá'
];

const AssistantHeaderSearchInput = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );
    const [searchValue, setSearchValue] = useState('');
    const [isFocused, setFocused] = useState(false);
    const [isModified, setModified] = useState(false);
    const [placeholder, setPlaceholder] = useState(examplePlaceholders[0]);
    const [isFadingOut, setFadingOut] = useState(false);
    const [currentExample, setCurrentExample] = useState(1);
    const placeholderTimeout = useRef<number | null>(null);

    useEffect(() => {
        if (
            mainFeatureItem &&
            mainFeatureItem.featureType === 'product-search-by-text'
        ) {
            setSearchValue(mainFeatureItem.searchText);
        } else {
            setSearchValue('');
        }
        setModified(false);
    }, [mainFeatureItem?.featureUuid]);

    useEffect(() => {
        const changePlaceholder = () => {
            setFadingOut(true);
            setTimeout(() => {
                setPlaceholder(examplePlaceholders[currentExample]);
                setFadingOut(false);
                setCurrentExample(
                    (currentExample + 1) % examplePlaceholders.length
                );
            }, 500); // Fade-out duration
        };

        placeholderTimeout.current = window.setInterval(
            changePlaceholder,
            3000
        );

        return () => {
            if (placeholderTimeout.current) {
                clearInterval(placeholderTimeout.current);
            }
        };
    }, [currentExample]);

    if (!mainFeatureItem) {
        return null;
    }

    const fileInputRef = useRef<HTMLInputElement>(null);

    return (
        <div
            className={`bri-AssistantHeaderSearchInput ${
                isFocused ? 'bri-focused' : ''
            } ${isModified ? 'bri-modified' : ''}`}
        >
            <Icon
                iconId="search-magic"
                className="bri-AssistantHeaderSearchInput-search-icon"
            />
            <input
                value={searchValue}
                onChange={e => {
                    setSearchValue((e.target as HTMLInputElement).value);
                    setModified(true);
                }}
                onKeyDown={e => {
                    if (e.key === 'Enter' && searchValue.trim().length > 0) {
                        void dispatch.featureState.startFeature({
                            featureType: 'product-search-by-text',
                            searchText: searchValue.trim()
                        });
                    }
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                type="text"
                maxLength={200}
                className="bri-AssistantHeaderSearchInput-input bri-DisplayFont"
            />
            {!isFocused && !searchValue && (
                <span className="bri-AssistantHeaderSearchInput-placeholder bri-DisplayFont">
                    <span className="bri-AssistantHeaderSearchInput-placeholder-prefix">
                        {t('PRODUCT_SEARCH_INPUT_PLACEHOLDER_PREFIX')}
                    </span>
                    <span
                        className={`bri-AssistantHeaderSearchInput-placeholder-text ${
                            isFadingOut ? 'bri-fading-out' : 'bri-fading-in'
                        }`}
                    >
                        {placeholder}
                    </span>
                </span>
            )}

            <IconButton
                variant="default"
                className="bri-AssistantHeaderSearchInput-end-icon"
                iconId={searchValue.trim() ? 'cancel' : 'search-image'}
                title={
                    searchValue.trim()
                        ? t('CLEAR')
                        : t('PRODUCT_SEARCH_BY_IMAGE')
                }
                onClick={() => {
                    if (!searchValue.trim()) {
                        fileInputRef.current?.click();
                    } else {
                        setSearchValue('');
                    }
                }}
                disabled={false}
            />
            <ImageUpload ref={fileInputRef} />
        </div>
    );
};

export default AssistantHeaderSearchInput;
